import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiCode, FiPause, FiPlay, FiRotateCcw } from "react-icons/fi";
import { spherePoints, rotatePoint } from "./globeMath";
import { iconMap } from "./skillIcons";

const SkillGlobe = ({ skills }) => {
  const stage = useRef(null);
  const labels = useRef([]);
  const rotation = useRef({ yaw: 0.35, pitch: -0.15 });
  const drag = useRef(null);
  const hovering = useRef(false);
  const focused = useRef(false);
  const draw = useRef(() => {});
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const points = useMemo(() => spherePoints(skills.length), [skills.length]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const element = stage.current;
    let size = element.clientWidth;
    let frame;
    let last = 0;
    let visible = false;
    let disposed = false;
    let dimensions = [];
    const measure = () => {
      size = element.clientWidth;
      dimensions = labels.current.map((label) => ({ width: label?.offsetWidth || 0, height: label?.offsetHeight || 0 }));
    };
    const paint = () => {
      const widest = Math.max(0, ...dimensions.map(({ width }) => width));
      const radius = size <= 480 ? Math.max(0, Math.min(size * 0.37, size / 2 - widest * 0.52 - 6)) : size * 0.37;
      const occupied = [];
      points.map((point, index) => ({ ...rotatePoint(point, rotation.current.yaw, rotation.current.pitch), index }))
        .sort((a, b) => b.z - a.z)
        .forEach(({ x, y, z, index }) => {
          const label = labels.current[index];
          if (!label) return;
          const scale = 0.72 + (z + 1) * 0.16;
          const left = size / 2 + x * radius;
          const top = size / 2 + y * radius;
          const width = dimensions[index].width * scale;
          const height = dimensions[index].height * scale;
          const rect = { left: left - width / 2 - 2, right: left + width / 2 + 2, top: top - height / 2 - 2, bottom: top + height / 2 + 2 };
          const overlaps = occupied.some((other) => rect.left < other.right && rect.right > other.left && rect.top < other.bottom && rect.bottom > other.top);
          const shown = z > -0.1 && !overlaps;
          if (shown) occupied.push(rect);
          // Projection values change each frame; transforms keep animation off layout.
          label.style.transform = `translate(${left}px, ${top}px) translate(-50%, -50%) scale(${scale})`;
          label.style.opacity = shown ? String(0.65 + (z + 0.1) * 0.3) : "0";
          label.style.zIndex = String(Math.round((z + 1) * 100));
        });
    };
    draw.current = paint;
    const tick = (time) => {
      const delta = last ? Math.min(time - last, 40) : 0;
      last = time;
      if (!paused && !reducedMotion && !hovering.current && !focused.current && !drag.current) {
        rotation.current.yaw += delta * 0.00009;
        paint();
      }
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      last = 0;
      if (visible && !document.hidden && !paused && !reducedMotion) frame = requestAnimationFrame(tick);
    };
    measure();
    const resize = new ResizeObserver(() => { measure(); paint(); });
    resize.observe(element);
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); });
    observer.observe(element);
    document.addEventListener("visibilitychange", sync);
    document.fonts?.ready.then(() => {
      if (!disposed) { measure(); paint(); }
    });
    paint();
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resize.disconnect();
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      draw.current = () => {};
    };
  }, [points, paused, reducedMotion]);

  const stopDragging = (event) => {
    if (drag.current?.id !== event.pointerId) return;
    if (drag.current.type === "touch") { hovering.current = false; focused.current = false; }
    drag.current = null;
    stage.current.removeAttribute("data-dragging");
    if (stage.current.hasPointerCapture(event.pointerId)) stage.current.releasePointerCapture(event.pointerId);
  };

  return (
    <Wrapper>
      <Stage
        ref={stage}
        role="group"
        tabIndex={0}
        aria-label="Interactive skill globe"
        aria-describedby="globe-instructions"
        onPointerEnter={(event) => { hovering.current = event.pointerType !== "touch"; }}
        onPointerLeave={() => { hovering.current = false; }}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; }}
        onPointerDown={(event) => {
          if (!event.isPrimary || event.button !== 0) return;
          if (event.pointerType !== "touch") stage.current.focus({ preventScroll: true });
          stage.current.setPointerCapture(event.pointerId);
          stage.current.setAttribute("data-dragging", "true");
          drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, type: event.pointerType };
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          if (!start || start.id !== event.pointerId) return;
          rotation.current.yaw += (event.clientX - start.x) * 0.008;
          rotation.current.pitch -= (event.clientY - start.y) * 0.008;
          drag.current = { ...start, x: event.clientX, y: event.clientY };
          draw.current();
        }}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onLostPointerCapture={stopDragging}
        onKeyDown={(event) => {
          const directions = { ArrowLeft: [-0.15, 0], ArrowRight: [0.15, 0], ArrowUp: [0, 0.15], ArrowDown: [0, -0.15] };
          if (!directions[event.key]) return;
          event.preventDefault();
          rotation.current.yaw += directions[event.key][0];
          rotation.current.pitch += directions[event.key][1];
          draw.current();
        }}
      >
        <Sphere aria-hidden="true"><i /><i /><i /></Sphere>
        <Labels aria-hidden="true">
          {skills.map((skill, index) => {
            const Icon = iconMap[skill.Icon] || FiCode;
            return <Label key={skill.Skill} ref={(element) => { labels.current[index] = element; }} $core={skill.Underline}><Icon /><span>{skill.Skill}</span></Label>;
          })}
        </Labels>
      </Stage>
      <Controls>
        <button type="button" disabled={reducedMotion} aria-pressed={paused || reducedMotion} onClick={() => setPaused((value) => !value)}>
          {paused || reducedMotion ? <FiPlay aria-hidden="true" /> : <FiPause aria-hidden="true" />}
          {reducedMotion ? "Motion reduced" : paused ? "Resume rotation" : "Pause rotation"}
        </button>
        <button type="button" onClick={() => { rotation.current = { yaw: 0.35, pitch: -0.15 }; draw.current(); }}><FiRotateCcw aria-hidden="true" /> Reset</button>
      </Controls>
      <Hint id="globe-instructions"><span className="mouse-hint">Drag to rotate · Use arrow keys when focused</span><span className="touch-hint">Drag with one finger in any direction to rotate.<br />Swipe outside the globe to scroll the page.</span></Hint>
    </Wrapper>
  );
};

export default SkillGlobe;

const Wrapper = styled.div`
  min-width: 0;
  @media (max-width: 599px) { max-width: 420px; margin-inline: auto; }
`;
const Stage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  border-radius: 50%;
  &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }
  &[data-dragging] { cursor: grabbing; }
`;
const Sphere = styled.div`
  position: absolute;
  inset: 12%;
  border: 1px solid #c9ded3;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #f8fcfa 0%, #eaf5ef 65%, #dceee3 100%);
  box-shadow: inset -20px -10px 50px #c6dfcf55, 0 24px 60px -28px #315b3f33;
  pointer-events: none;
  i { position: absolute; inset: 0; border: 1px solid #bad4c466; border-radius: 50%; }
  i:nth-child(1) { transform: scaleX(0.45); }
  i:nth-child(2) { transform: scaleY(0.45) rotate(-25deg); }
  i:nth-child(3) { transform: rotate(45deg) scaleX(0.65); }
`;
const Labels = styled.div`position: absolute; inset: 0; pointer-events: none;`;
const Label = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.65rem;
  border: 1px solid ${({ $core }) => $core ? "#accfbc" : "#dce8e0"};
  border-radius: 999px;
  background: ${({ $core }) => $core ? "#183c32" : "#fff"};
  color: ${({ $core }) => $core ? "#f0fff6" : "#354b42"};
  box-shadow: 0 3px 10px #183c320a;
  font-size: clamp(0.6rem, 1.1vw, 0.78rem);
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  svg { width: 1.1em; height: 1.1em; flex-shrink: 0; }
  @media (max-width: 599px) { padding: 0.4rem 0.5rem; gap: 0.3rem; font-size: 0.75rem; }
`;
const Controls = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  button {
    display: inline-flex; align-items: center; gap: 0.5rem;
    min-height: 44px; padding: 0.5rem 0.85rem;
    border: 1px solid #d6e4db; border-radius: 999px;
    background: #fff; color: #354b42; font-size: 0.7rem; cursor: pointer;
    &:hover:not(:disabled) { background: #eaf5ef; }
    &:disabled { cursor: default; color: #66776c; }
    &:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }
  }
  @media (max-width: 599px) {
    display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    button { justify-content: center; padding-inline: 0.5rem; font-size: 0.75rem; min-height: 48px; }
  }
`;
const Hint = styled.p`
  margin-top: 0.9rem; color: #65736b; font-size: 0.65rem; text-align: center; line-height: 1.8;
  .touch-hint { display: none; }
  @media (pointer: coarse), (max-width: 599px) {
    font-size: 0.7rem;
    .mouse-hint { display: none; }
    .touch-hint { display: inline; }
  }
`;
